import { colliders } from './state.js';

// ============================================================
//  Равномерная сетка по XZ над боксами карты — «широкая фаза» для всего,
//  что раньше перебирало `colliders` целиком.
//
//  ⚠️ ЗАЧЕМ. Пока карта была рукописным блокаутом (57 боксов), перебор всех
//  боксов в лоб стоил доли микросекунды и был честнее любой оптимизации.
//  Импортированная de_dust2 — 1743 бокса, и те же самые циклы стали стоить
//  ~5 мс на кадр: физика гоняет их по три раза за шаг (proneClear, опора,
//  выталкивание), а reconciliation переигрывает историю вводов целиком —
//  на пинге 250 мс это ещё ×15 сверху, 30 раз в секунду.
//
//  Сетка НЕ меняет ответы: она лишь сужает список кандидатов до тех, кто
//  физически может оказаться рядом. Формулы остались там же, где были
//  (player.js, grenades.js) — то есть зеркала с Rust-сервером не тронуты.
//  Сервер свои циклы гоняет в нативном коде и пока справляется; если начнёт
//  упираться — там нужна такая же сетка, а не другая математика.
// ============================================================

/** Сторона клетки, м. 4 м — компромисс: мельче даёт много клеток на крупный
 *  бокс (плита пола попадает во все), крупнее возвращает лишних кандидатов. */
const CELL = 4;

let minX = 0, minZ = 0, nx = 1, nz = 1;
/** cells[i] — массив индексов боксов, задевающих клетку i */
let cells = [];

/** Штампы «этот бокс уже отдан в текущем запросе» — вместо Set на каждый вызов.
 *  Крупный бокс лежит в сотнях клеток, и без штампа обработался бы сотни раз. */
let stamp = new Int32Array(0);
let stampGen = 0;

const cx = (x) => Math.max(0, Math.min(nx - 1, Math.floor((x - minX) / CELL)));
const cz = (z) => Math.max(0, Math.min(nz - 1, Math.floor((z - minZ) / CELL)));

/** Построить сетку. Вызывается один раз, после того как world.js наполнил colliders. */
export function buildGrid() {
  let maxX = -Infinity, maxZ = -Infinity;
  minX = Infinity; minZ = Infinity;
  for (const c of colliders) {
    if (c.min.x < minX) minX = c.min.x;
    if (c.min.z < minZ) minZ = c.min.z;
    if (c.max.x > maxX) maxX = c.max.x;
    if (c.max.z > maxZ) maxZ = c.max.z;
  }
  nx = Math.max(1, Math.ceil((maxX - minX) / CELL) + 1);
  nz = Math.max(1, Math.ceil((maxZ - minZ) / CELL) + 1);
  cells = new Array(nx * nz);
  for (let i = 0; i < cells.length; i++) cells[i] = [];
  for (let i = 0; i < colliders.length; i++) {
    const c = colliders[i];
    const i0 = cx(c.min.x), i1 = cx(c.max.x), j0 = cz(c.min.z), j1 = cz(c.max.z);
    for (let j = j0; j <= j1; j++) for (let i2 = i0; i2 <= i1; i2++) cells[j * nx + i2].push(i);
  }
  stamp = new Int32Array(colliders.length);
  stampGen = 0;
}

/** Буфер кандидатов одного запроса. Общий и переиспользуемый: nearBox зовут
 *  несколько раз за шаг физики, и новый массив на каждый вызов кормил бы GC. */
const _hits = [];

/**
 * Обход боксов, задевающих прямоугольник XZ. Каждый бокс отдаётся один раз,
 * В ПОРЯДКЕ ИХ НОМЕРОВ В map.json.
 *
 * ⚠️ Порядок здесь — не косметика, а условие совпадения с сервером.
 * `resolveCollisions` выталкивает игрока накопительно: в углу, где толкают
 * сразу два бокса, итог зависит от того, кто толкнул первым. Сервер
 * (movement.rs) идёт по списку карты подряд, поэтому и мы обязаны выдавать
 * кандидатов в том же порядке — иначе позиции разойдутся на миллиметры,
 * reconciliation увидит расхождение и начнёт дёргать игрока на ровном месте.
 * Сортировка десятка чисел на запрос дешевле такого класса багов.
 *
 * ⚠️ Не реентерабельно: буфер один. Вызывать nearBox изнутри cb другого
 * nearBox нельзя.
 */
export function nearBox(x0, z0, x1, z1, cb) {
  const g = ++stampGen;
  const i0 = cx(x0), i1 = cx(x1), j0 = cz(z0), j1 = cz(z1);
  _hits.length = 0;
  for (let j = j0; j <= j1; j++) {
    for (let i = i0; i <= i1; i++) {
      const list = cells[j * nx + i];
      for (let k = 0; k < list.length; k++) {
        const idx = list[k];
        if (stamp[idx] === g) continue;
        stamp[idx] = g;
        _hits.push(idx);
      }
    }
  }
  _hits.sort((a, b) => a - b);
  for (let k = 0; k < _hits.length; k++) cb(colliders[_hits[k]]);
}

/** Точечный запрос с допуском — частый случай (опора, отскок, лужа крови). */
export function nearPoint(x, z, r, cb) {
  nearBox(x - r, z - r, x + r, z + r, cb);
}

/**
 * Обход боксов вдоль луча в проекции на XZ (DDA по клеткам), от ближней
 * клетки к дальней. `cb(c)` может вернуть true — тогда обход прекращается
 * ПОСЛЕ текущей клетки (не сразу: в одной клетке может лежать бокс ближе).
 *
 * ⚠️ Луч почти вертикальный (dx≈dz≈0) вырождается в одну клетку — это верно:
 * по XZ он никуда не уходит.
 */
export function nearRay(ox, oz, dx, dz, maxDist, cb) {
  const g = ++stampGen;
  let i = cx(ox), j = cz(oz);
  const flat = Math.hypot(dx, dz);
  if (flat < 1e-9) { sweepCell(i, j, g, cb); return; }

  const stepI = dx > 0 ? 1 : -1, stepJ = dz > 0 ? 1 : -1;
  // расстояние (вдоль луча, в метрах по XZ) до следующей границы клетки
  const tDeltaX = Math.abs(dx) < 1e-9 ? Infinity : Math.abs(CELL / dx);
  const tDeltaZ = Math.abs(dz) < 1e-9 ? Infinity : Math.abs(CELL / dz);
  const bx = minX + (i + (stepI > 0 ? 1 : 0)) * CELL;
  const bz = minZ + (j + (stepJ > 0 ? 1 : 0)) * CELL;
  let tMaxX = Math.abs(dx) < 1e-9 ? Infinity : (bx - ox) / dx;
  let tMaxZ = Math.abs(dz) < 1e-9 ? Infinity : (bz - oz) / dz;

  // maxDist задан вдоль полного (3D) луча, а шагаем мы по проекции на XZ —
  // переводим предел в ту же меру, иначе крутой луч оборвётся слишком рано.
  const limit = maxDist * flat;
  let t = 0, guard = 0;
  for (;;) {
    if (sweepCell(i, j, g, cb)) return;
    if (t > limit || ++guard > nx + nz + 4) return;
    if (tMaxX < tMaxZ) { i += stepI; t = tMaxX; tMaxX += tDeltaX; } else { j += stepJ; t = tMaxZ; tMaxZ += tDeltaZ; }
    if (i < 0 || i >= nx || j < 0 || j >= nz) return;
  }
}

function sweepCell(i, j, g, cb) {
  const list = cells[j * nx + i];
  let stop = false;
  for (let k = 0; k < list.length; k++) {
    const idx = list[k];
    if (stamp[idx] === g) continue;
    stamp[idx] = g;
    if (cb(colliders[idx])) stop = true;
  }
  return stop;
}
